/**
 * Marketplace Charts Component
 * Reusable time-based analytics charts
 */
import React from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl">
        <p className="text-white font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.name.includes('Revenue') ? `$${entry.value}` : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Lead Trends Line Chart
export const LeadTrendsChart = ({ data, height = 300 }) => {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis 
          dataKey="label" 
          stroke="#9CA3AF" 
          fontSize={12}
          tickLine={false}
        />
        <YAxis 
          stroke="#9CA3AF" 
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ paddingTop: '10px' }}
          formatter={(value) => <span className="text-gray-300 text-sm">{value}</span>}
        />
        <Line 
          type="monotone" 
          dataKey="finance_leads" 
          name="Finance Leads"
          stroke="#10B981" 
          strokeWidth={2}
          dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
        />
        <Line 
          type="monotone" 
          dataKey="insurance_leads" 
          name="Insurance Leads"
          stroke="#6366F1" 
          strokeWidth={2}
          dot={{ fill: '#6366F1', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6, stroke: '#6366F1', strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Revenue Trends Area Chart
export const RevenueTrendsChart = ({ data, height = 300 }) => {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <defs>
          <linearGradient id="financeGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
          </linearGradient>
          <linearGradient id="insuranceGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis 
          dataKey="label" 
          stroke="#9CA3AF" 
          fontSize={12}
          tickLine={false}
        />
        <YAxis 
          stroke="#9CA3AF" 
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `$${value}`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ paddingTop: '10px' }}
          formatter={(value) => <span className="text-gray-300 text-sm">{value}</span>}
        />
        <Area 
          type="monotone" 
          dataKey="finance_revenue" 
          name="Finance Revenue"
          stroke="#10B981" 
          strokeWidth={2}
          fill="url(#financeGradient)"
        />
        <Area 
          type="monotone" 
          dataKey="insurance_revenue" 
          name="Insurance Revenue"
          stroke="#6366F1" 
          strokeWidth={2}
          fill="url(#insuranceGradient)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Combined Bar Chart
export const CombinedBarChart = ({ data, height = 300 }) => {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis 
          dataKey="label" 
          stroke="#9CA3AF" 
          fontSize={12}
          tickLine={false}
        />
        <YAxis 
          stroke="#9CA3AF" 
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ paddingTop: '10px' }}
          formatter={(value) => <span className="text-gray-300 text-sm">{value}</span>}
        />
        <Bar 
          dataKey="finance_leads" 
          name="Finance"
          fill="#10B981" 
          radius={[4, 4, 0, 0]}
        />
        <Bar 
          dataKey="insurance_leads" 
          name="Insurance"
          fill="#6366F1" 
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Total Revenue Line Chart
export const TotalRevenueChart = ({ data, height = 300 }) => {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <defs>
          <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4}/>
            <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis 
          dataKey="label" 
          stroke="#9CA3AF" 
          fontSize={12}
          tickLine={false}
        />
        <YAxis 
          stroke="#9CA3AF" 
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `$${value}`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area 
          type="monotone" 
          dataKey="total_revenue" 
          name="Total Revenue"
          stroke="#8B5CF6" 
          strokeWidth={3}
          fill="url(#totalGradient)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default {
  LeadTrendsChart,
  RevenueTrendsChart,
  CombinedBarChart,
  TotalRevenueChart
};
